import { useHistory, useParams } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import React, { lazy, useCallback, useEffect, useState, Suspense, useRef } from 'react'
import { usePendingInvites, useGroupsContext } from '@fs/zion-tree-groups'
import { useEvent } from '@fs/zion-frontend-friends'
import { Title } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import useGroupMembers from '../services/useGroupMembers'
import useGroup from '../services/useGroup'
import GroupLimitsProvider from '../providers/GroupLimitsProvider'
import PrivatePersonCountProvider from '../providers/PrivatePersonCountProvider'
import usePrivatePersonCount from '../services/usePrivatePersonCount'
import MembersProvider from '../providers/MembersProvider'
import GroupDetailsPageSkeletons from './GroupDetailsPageSkeletons'
import GroupManagementGroupProvider from '../providers/GroupManagementGroupProvider'
import StartingPersonProvider from '../providers/StartingPersonProvider'
import PendingInvitesProvider from '../providers/PendingInvitesProvider'
import GettingStartedProvider from '../providers/GettingStartedProvider'
import GedcomDataProvider from '../providers/GedcomDataProvider'
import getGedcomData from '../services/getGedcomData'
import LoadedGroupDetailsPage from './LoadedGroupDetailsPage'
import { useUserIsDataAdmin } from '../providers/DataAdminProvider'

const UnauthorizedPage = lazy(() => import('./UnauthorizedPage'))

export default function GroupDetailsPage() {
  const { groupId, subPage, tab } = useParams()
  const { currentTreeId, selectTree, treeDetails } = useGroupsContext()
  const { data: group, refetch: refetchGroupDetails, loading: loadingGroup } = useGroup(groupId)
  const user = useUser()
  const {
    data: { members: allMembers, pendingMembers },
    refetch: refetchGroupMembers,
    error: errorGroupMembers,
  } = useGroupMembers(groupId)
  const isCurrent = useRef()
  const currentMemberDetails = allMembers?.find((member) => member.memberCisId === user.cisId)
  const treeId = group?.treeIds?.[0]
  const { privatePersonCount, isLoadingPrivatePersonCount, refetchPrivatePersonCount } = usePrivatePersonCount(treeId)
  const history = useHistory()
  const [gedcomData, setGedcomData] = useState()
  const hasInitFetchGedcomDataForTreeId = useRef(false)
  const userIsOwner =
    currentMemberDetails && treeDetails && currentMemberDetails?.memberCisId === treeDetails.ownerCisId
  const isResearcherGroup = group?.types.includes('RESEARCHER_TREE')
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const [t] = useTranslation()

  const fetchGedcomData = useCallback(() => {
    if (!userIsOwner) return
    getGedcomData(group.treeIds?.[0])
      .then((data) => {
        isCurrent.current && setGedcomData(data)
        return undefined
      })
      .catch(console.error)
  }, [group?.treeIds, setGedcomData, userIsOwner])

  useEffect(() => {
    isCurrent.current = true
    if (
      group &&
      typeof group.treeIds?.[0] === 'string' &&
      isResearcherGroup &&
      hasInitFetchGedcomDataForTreeId.current !== group.treeIds[0] &&
      userIsOwner
    ) {
      hasInitFetchGedcomDataForTreeId.current = group.treeIds[0]
      fetchGedcomData()
    }

    return () => {
      isCurrent.current = false
    }
  }, [fetchGedcomData, group, isResearcherGroup, userIsOwner])

  useEffect(() => {
    if (subPage === 'research-tree' && groupId && !tab) {
      history.replace(`/research-tree/${groupId}/details`)
    }
  }, [groupId, history, subPage, tab])

  useEffect(() => {
    const isPartOfGroup = Boolean(
      currentMemberDetails && (currentMemberDetails?.status === 'MEMBER' || currentMemberDetails?.status === 'ADMIN')
    )
    // This sets the current group's treeId as the session's treeId, making sure that the user belongs to the group
    if (treeId && (isPartOfGroup || userIsDataAdmin) && currentTreeId !== treeId && selectTree) {
      selectTree(treeId)
    }
  }, [currentMemberDetails, currentTreeId, selectTree, treeId, userIsDataAdmin])

  const onLeaveGroup = useEvent(() => {
    history.push('/family')
  })

  let getPendingInvites = false

  if (group && (currentMemberDetails || userIsDataAdmin)) {
    if (isResearcherGroup) {
      getPendingInvites = currentMemberDetails?.status === 'ADMIN' || userIsDataAdmin
    } else {
      getPendingInvites = true
    }
  }

  const { singleUseInvites, multiUseInvites, refetchInvites } = usePendingInvites({
    groupId: group?.id,
    willFetch: getPendingInvites,
  })

  if (errorGroupMembers?.response?.status === 403)
    return (
      <Suspense fallback="">
        <UnauthorizedPage />
      </Suspense>
    )

  if (
    loadingGroup ||
    (treeId && (isLoadingPrivatePersonCount || !treeDetails || treeDetails.treeId !== treeId)) ||
    (!currentMemberDetails && !userIsDataAdmin)
  ) {
    return <GroupDetailsPageSkeletons subPage={subPage} />
  }

  return (
    <>
      <Title>{t('group-management.group-details-page.title', { groupName: group.name })}</Title>
      <StartingPersonProvider treeId={group?.treeIds?.[0]} types={group?.types}>
        <GettingStartedProvider>
          <GroupLimitsProvider>
            <PrivatePersonCountProvider
              privatePersonCount={privatePersonCount}
              refetchPrivatePersonCount={refetchPrivatePersonCount}
            >
              <GroupManagementGroupProvider
                group={group}
                refetchGroup={refetchGroupDetails}
                onLeaveGroup={onLeaveGroup}
              >
                <MembersProvider
                  allMembers={allMembers}
                  pendingMembers={pendingMembers}
                  currentMemberDetails={currentMemberDetails}
                  refetchGroupMembers={refetchGroupMembers}
                  userIsDataAdmin={userIsDataAdmin}
                >
                  <PendingInvitesProvider
                    group={group}
                    singleUseInvites={singleUseInvites}
                    refetchInvites={refetchInvites}
                    multiUseInvites={multiUseInvites}
                  >
                    <GedcomDataProvider gedcomData={gedcomData} refetchGedcomData={fetchGedcomData}>
                      <LoadedGroupDetailsPage />
                    </GedcomDataProvider>
                  </PendingInvitesProvider>
                </MembersProvider>
              </GroupManagementGroupProvider>
            </PrivatePersonCountProvider>
          </GroupLimitsProvider>
        </GettingStartedProvider>
      </StartingPersonProvider>
    </>
  )
}
